// @tg-seq: 10000
// @architect-approved: HARDCODED_COLORS - This IS the canonical DIRM directory-host
// pattern component (all /dirm_ card-pattern directories route through it), but its
// own filename doesn't match the '/dirm_' PATTERN_EXEMPTIONS path substring. Its
// near-black text hierarchy (rgba(19,17,9,x)), white card surfaces (rgba(255,255,255,x)),
// and warning-banner high-contrast colors (#263238, #FFC107/#FF9800/#F57C00) are
// deliberate structural/branding chrome, same spirit as the /dirm_ exemption.
/**
 * Techgacity Center Welcome — The Front Door
 *
 * ARCHITECTURE: Universal DIRM card pattern host (pattern_id = 3)
 * Renders navigation cards from directory-navigation-manifest.json
 * All TGC/LCV card-pattern directories route to this ONE component
 *
 * Database CR2 Sequence_Num: 10000, Domain: TGC, Floor: 0, Subfloor: 0
 *
 * Created: 2026-03-21
 * Updated: 2026-06-01 — Merged template logic directly, added branding context
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    Tooltip,
    Typography,
} from '@mui/material';

import { useBrandingContext } from '../../StylesLibrary/hooks/useBrandingContext';
import ConstructionIcon from '@mui/icons-material/Construction';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RouteIcon from '@mui/icons-material/Route';
import { getTsxFileName } from '../../utils/componentIdentity';
import { handleManifestButtonClick } from '../../utils/directoryNavigation';
import { useNavigationChrome } from '../../utils/useNavigationChrome';
import { resolveIcon } from '../ComponentTemplates/DirectoryPatterns/icon-map';
import { useDirectoryHost } from '../ComponentTemplates/DirectoryPatterns/useDirectoryHost';

const Self = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { collection } = useBrandingContext();
    const chrome = useNavigationChrome('dirm', 10000); // Database-driven navigation chrome

    // 🔒 LOCKED PATTERN: Use useDirectoryHost for consistent directory resolution
    // Encapsulates: location.state.dirSeq → manifest resolution → component fallback
    const dir = useDirectoryHost(10000);

    // Graceful bg image — only render if the file actually loads
    const [bgReady, setBgReady] = useState(false);
    useEffect(() => {
        if (!dir?.backgroundImagePath) {
            setBgReady(false);
            return;
        }
        const img = new Image();
        img.onload = () => setBgReady(true);
        img.onerror = () => setBgReady(false);
        img.src = dir.backgroundImagePath;
    }, [dir?.backgroundImagePath]);

    if (!dir) {
        return (
            <Container maxWidth="lg" sx={{ mt: 8 }}>
                <Typography color="error" variant="h6" gutterBottom>
                    Directory 10000 not found in manifest
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ mt: 2, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                >
                    {`Debug Info:
- Current Route: ${location.pathname}
- Component Seq: 10000
- TSX File: nav_tgc_10000_dirm_directory_host.tsx
- Location State: ${JSON.stringify(location.state, null, 2)}

Manifest Lookup:
- useDirectoryHost(10000): ${dir ? 'FOUND' : 'NULL'}
- Manifest loaded: ${typeof window !== 'undefined' ? 'YES' : 'NO'}`}
                </Typography>
            </Container>
        );
    }

    const buttons = dir.buttons;

    // Calculate grid columns for MUI Grid (12-column system)
    // Priority: 1. dir.gridColumns (DB setting), 2. Smart calc based on button count
    // Grid columns → md value: 1 col = md:12, 2 col = md:6, 3 col = md:4, 4 col = md:3
    const mdCols = dir.gridColumns
        ? Math.floor(12 / dir.gridColumns) // Use DB setting if available
        : buttons.length >= 3
          ? 4
          : buttons.length === 2
            ? 6
            : 12; // Fallback smart calc

    return (
        <Box
            sx={{
                background: chrome.dirm.container.outer.backgroundColor,
                minHeight: chrome.dirm.container.outer.minHeight,
                py: 6,
            }}
        >
            <Container maxWidth="lg">
                <Box
                    sx={{
                        background: chrome.dirm.container.inner.backgroundColor,
                        borderRadius: chrome.dirm.container.inner.borderRadius,
                        p: 6,
                        ...(bgReady &&
                            dir.backgroundImagePath && {
                                backgroundImage: `url(${dir.backgroundImagePath})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundBlendMode: 'soft-light',
                            }),
                    }}
                >
                    {/* Under Construction Banner — DB-driven via Show_Under_Construction */}
                    {dir.showUnderConstruction && (
                        <Box
                            sx={{
                                background: 'linear-gradient(135deg, #FFC107 0%, #FF9800 100%)',
                                borderRadius: 3,
                                p: 3,
                                mb: 4,
                                textAlign: 'center',
                                border: '2px solid #F57C00',
                            }}
                        >
                            <ConstructionIcon sx={{ fontSize: 56, color: '#263238', mb: 1 }} />
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#263238' }}>
                                🚧 UNDER CONSTRUCTION
                            </Typography>
                        </Box>
                    )}
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Box
                            component="img"
                            src="/branding/techgacity-mark.svg"
                            alt="TechGacity mark"
                            sx={{
                                width: 80,
                                height: 80,
                                mb: 2,
                            }}
                        />
                        {dir.headerText && (
                            <Typography
                                variant="h2"
                                component="h1"
                                gutterBottom
                                sx={{
                                    fontSize: '2.5rem',
                                    fontWeight: 700,
                                    color: 'rgba(19, 17, 9, 0.95)',
                                }}
                            >
                                {dir.headerText}
                            </Typography>
                        )}
                        {dir.headerSubtitle && (
                            <Typography
                                variant="h5"
                                gutterBottom
                                sx={{ fontSize: '1.25rem', color: 'rgba(19, 17, 9, 0.7)' }}
                            >
                                {dir.headerSubtitle}
                            </Typography>
                        )}
                        {dir.welcomeMessage && (
                            <Typography
                                variant="body1"
                                sx={{
                                    mt: 2,
                                    maxWidth: 600,
                                    mx: 'auto',
                                    fontSize: '1.15rem',
                                    color: 'rgba(19, 17, 9, 0.7)',
                                    lineHeight: 1.6,
                                }}
                            >
                                {dir.welcomeMessage}
                            </Typography>
                        )}
                    </Box>
                    {/* Navigation Cards */}
                    {buttons.length > 0 ? (
                        <Grid container spacing={3}>
                            {buttons.map((btn) => (
                                <Grid
                                    key={btn.assignmentId}
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                        md: mdCols,
                                    }}
                                >
                                    <Card
                                        sx={{
                                            height: '100%',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            background: 'rgba(255, 255, 255, 0.9)',
                                            border: '1px solid rgba(19, 17, 9, 0.1)',
                                            transition: 'all 0.3s',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
                                            },
                                        }}
                                        onClick={() =>
                                            handleManifestButtonClick(
                                                btn,
                                                navigate,
                                                dir.directorySequenceNum,
                                                location.pathname,
                                            )
                                        }
                                    >
                                        {/* Route tooltip — directories show target name, functional components show filename */}
                                        <Tooltip
                                            title={
                                                btn.target_dir_sequence_num
                                                    ? btn.target_directory_name ||
                                                      btn.target_header_text ||
                                                      `${btn.prefix}-${btn.child_sequence_num}`
                                                    : btn.tsx_file_name_target
                                            }
                                            placement="top"
                                            arrow
                                        >
                                            <RouteIcon
                                                sx={{
                                                    position: 'absolute',
                                                    top: 6,
                                                    right: 6,
                                                    fontSize: 16,
                                                    color: 'grey.500',
                                                    opacity: 0.4,
                                                    transition: 'opacity 0.2s, color 0.2s',
                                                    '&:hover': {
                                                        opacity: 1,
                                                        color:
                                                            collection?.primaryColor ||
                                                            'primary.main',
                                                    },
                                                    zIndex: 1,
                                                }}
                                            />
                                        </Tooltip>
                                        <CardContent>
                                            <Box
                                                sx={{
                                                    color:
                                                        btn.iconColor ||
                                                        collection?.primaryColor ||
                                                        'primary.main',
                                                    mb: 2,
                                                }}
                                            >
                                                {resolveIcon(btn.displayIcon, { fontSize: 48 })}
                                            </Box>
                                            <Typography variant="h5" gutterBottom>
                                                {btn.target_dir_sequence_num
                                                    ? btn.target_directory_name ||
                                                      btn.target_header_text ||
                                                      btn.displayLabel
                                                    : btn.displayLabel}
                                            </Typography>
                                            {btn.displayDescription && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    {btn.displayDescription}
                                                </Typography>
                                            )}
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                sx={{
                                                    mt: 2,
                                                    borderColor:
                                                        collection?.primaryColor || 'primary.main',
                                                    color:
                                                        collection?.primaryColor || 'primary.main',
                                                    '&:hover': {
                                                        borderColor:
                                                            collection?.primaryColor ||
                                                            'primary.main',
                                                        background: `${collection?.primaryColor || 'rgba(25, 118, 210, 0.04)'}`,
                                                    },
                                                }}
                                            >
                                                {btn.target_dir_sequence_num
                                                    ? btn.target_directory_name ||
                                                      btn.target_header_text ||
                                                      btn.displayLabel
                                                    : btn.displayLabel}
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 4,
                            }}
                        >
                            <Typography variant="body1" sx={{ color: 'rgba(19, 17, 9, 0.5)' }}>
                                No pathways configured yet.
                            </Typography>
                        </Box>
                    )}
                    {/* Footer — identity stamp + optional custom text */}
                    <Box sx={{ mt: 4, p: 2, background: 'rgba(19, 17, 9, 0.03)', borderRadius: 1 }}>
                        {dir.footerText && (
                            <Typography
                                variant="body2"
                                sx={{ color: 'rgba(19, 17, 9, 0.6)' }}
                                gutterBottom
                            >
                                {dir.footerText}
                            </Typography>
                        )}
                        <Typography
                            variant="caption"
                            sx={{ color: 'rgba(19, 17, 9, 0.4)', fontFamily: 'monospace' }}
                        >
                            {dir.directoryName} • seq:{dir.componentSequenceNum} • {dir.tsxFileName}
                        </Typography>
                    </Box>
                    {/* Directory Description Banner — DB-driven via Show_Directory_Description */}
                    {dir.showDirectoryDescription && dir.directoryDescription && (
                        <Box
                            sx={{
                                mt: 4,
                                p: 3,
                                borderRadius: 2,
                                textAlign: 'center',
                                background: 'rgba(25, 118, 210, 0.81)',
                                border: '1px solid rgba(25, 118, 210, 0.2)',
                            }}
                        >
                            <InfoOutlinedIcon
                                sx={{
                                    fontSize: 32,
                                    color: collection?.primaryColor || 'primary.main',
                                    mb: 1,
                                }}
                            />
                            <Typography variant="body1" sx={{ color: 'rgba(19, 17, 9, 0.7)' }}>
                                {dir.directoryDescription}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Container>
        </Box>
    );
};

Self.displayName = getTsxFileName(import.meta.url);

export default Self;
